import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["triggerButton", "expandedView", "collapsedLayout", "expandedLayout", "toggleButton", "toggleButtonText"];

  static values = {
    valuationId: Number,
  }

  connect() {
    if (!this.hasExpandedViewTarget) return;

    const savedState = this.getSavedState();
    this.isOpen = savedState.isOpen !== undefined ? savedState.isOpen : true;
    this.textExpanded = savedState.textExpanded || false;

    if (this.isOpen) {
      this.showExpandedView();
    }

    if (this.textExpanded) {
      this.showExpandedLayout();
    }

    this.updateToggleButtonVisibility();
  }

  updateToggleButtonVisibility() {
    if (!this.hasToggleButtonTarget || !this.hasCollapsedLayoutTarget) return;

    const truncatedElements = this.collapsedLayoutTarget.querySelectorAll(".line-clamp-1");
    const hasOverflow = Array.from(truncatedElements).some(el => el.scrollHeight > el.clientHeight);

    if (hasOverflow) {
      this.toggleButtonTarget.classList.remove("hidden");
    } else {
      this.toggleButtonTarget.classList.add("hidden");
    }
  }

  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    // Update trigger button style
    this.triggerButtonTarget.classList.add("bg-lightGray-1000");
    this.triggerButtonTarget.classList.remove("bg-white");

    // Show expanded view
    this.expandedViewTarget.classList.remove("hidden");

    // Trigger zoom animation with slight delay
    requestAnimationFrame(() => {
      this.expandedViewTarget.classList.remove("opacity-0");
      this.expandedViewTarget.style.transform = "scale(1)";
    });

    this.isOpen = true;
    this.saveState();
  }

  close(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isOpen) return;

    // Trigger zoom out animation
    this.expandedViewTarget.classList.add("opacity-0");
    this.expandedViewTarget.style.transform = "scale(0.95)";

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.expandedViewTarget.classList.add("hidden");

      // Reset trigger button style
      this.triggerButtonTarget.classList.remove("bg-lightGray-1000");
      this.triggerButtonTarget.classList.add("bg-white");
    }, 300);

    this.isOpen = false;
    this.saveState();
  }

  toggleText(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.textExpanded) {
      this.collapseLayout();
    } else {
      this.expandLayout();
    }
  }

  expandLayout() {
    if (this.textExpanded) return;

    // Hide rows layout, show columns layout
    this.collapsedLayoutTarget.classList.add("hidden");
    this.expandedLayoutTarget.classList.remove("hidden");

    // Update button text
    this.toggleButtonTextTarget.textContent = "Show less";

    this.textExpanded = true;
    this.saveState();
  }

  collapseLayout() {
    if (!this.textExpanded) return;

    // Show rows layout, hide columns layout
    this.expandedLayoutTarget.classList.add("hidden");
    this.collapsedLayoutTarget.classList.remove("hidden");

    // Update button text
    this.toggleButtonTextTarget.textContent = "Show more";

    this.textExpanded = false;
    this.saveState();
  }

  // Helper methods for initial state restoration
  showExpandedView() {
    this.triggerButtonTarget.classList.add("bg-lightGray-1000");
    this.triggerButtonTarget.classList.remove("bg-white");
    this.expandedViewTarget.classList.remove("hidden", "opacity-0");
    this.expandedViewTarget.style.transform = "scale(1)";
  }

  showExpandedLayout() {
    this.collapsedLayoutTarget.classList.add("hidden");
    this.expandedLayoutTarget.classList.remove("hidden");
    this.toggleButtonTextTarget.textContent = "Show less";
  }

  // === LocalStorage persistence ===
  get storageKey() {
    return "priority-notes-banner-state-" + this.valuationIdValue;
  }

  saveState() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        isOpen: this.isOpen,
        textExpanded: this.textExpanded
      }));
    } catch (e) {
      console.warn("Could not save priority notes banner state:", e);
    }
  }

  getSavedState() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.warn("Could not restore priority notes banner state:", e);
      return {};
    }
  }
}
